import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpService } from '../_http/http.service';
import { SERVICE_URL } from '../../_urls/service.urls';

@Injectable({
    providedIn: 'root',
})
export class ShgProfileService {
    private baseUrl = `${environment.fdmApiBaseUrl}${SERVICE_URL.PROFILE}`;
    private baseTransUrl = `${environment.fdmApiBaseUrl}${SERVICE_URL.TRANSACTION}`;

    constructor(
        private httpService: HttpService,
        private http: HttpClient
    ) {}

    // Get meeting data by shg
    getMemberListingData(bodyData: any): Observable<any> {
        const url = `${this.baseTransUrl}meeting/shg-listing`;
        return this.httpService.getRecords(url, bodyData);
    }

    getMemberListData<T>(bodyData: any): Observable<T> {
        const url = `${this.baseTransUrl}meeting/shg-list`;
        return this.http.post<T>(url, bodyData);
    }

    // Get meeting summary data
    getSummaryData(shgId: any, meetingNumber: any): Observable<any> {
        const url = `${this.baseTransUrl}meeting/meeting-summary/${shgId}/${meetingNumber}`;
        return this.httpService.getRecords(url);
    }

    // SHG pending count grouped by panchayat_id
    getShgPendingCount(
        stateId: string | number,
        districtId: string | number,
        blockId: string | number
    ): Observable<Record<string, number>> {
        const url = `${this.baseUrl}shg/my-task-count`;
        return this.httpService.getRecords<Record<string, number>>(url, {
            stateId,
            districtId,
            blockId,
        });
    }

    // SHG Profile verification / My Task listing
    putshgDedupListingAngular(data: any): Observable<any> {
        const url = `${this.baseUrl}shg/my-task`;
        const inputData = {
            stateId: data.state_id,
            districtId: data.district_id,
            blockId: data.block_id,
        };

        return this.httpService.getRecords(url, inputData).pipe(
            map((responseData: any) => {
                let records: any = responseData;

                if (data.panchayat_id)
                    records = records.filter(
                        (item: any) =>
                            Number(item.panchayat_id) ===
                            Number(data.panchayat_id)
                    );

                if (data.village_id)
                    records = records.filter(
                        (item: any) =>
                            Number(item.village_id) == Number(data.village_id)
                    );

                if (data.shg_name)
                    records = records.filter(
                        (item: any) => item.shg_name == data.shg_name
                    );

                if (data.shg_code)
                    records = records.filter(
                        (item: any) => item.shg_code == data.shg_code
                    );

                if (data.sort_direction === 'ASC')
                    records.sort((a: any, b: any) => a.shg_id - b.shg_id);
                else records.sort((a: any, b: any) => b.shg_id - a.shg_id);

                return { recordsList: records, totalRecords: records.length };
            })
        );
    }

    // Get SHG Basic Details
    GetShgBasicDetails(shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/basic`;
        return this.http.get(url, { observe: 'body' });
    }

    // Get SHG member list
    GetShgMemberList(shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/members`;
        return this.httpService.getRecords(url);
    }

    // Get SHG Bank List
    GetShgBankList(shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/bank`;
        return this.httpService.getRecords(url);
    }
    // Get SHG Bank List
    GetShgPhoneList(shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/phone`;
        return this.httpService.getRecords(url);
    }

    // Get Bank Branch by IFSC Code
    getBankBranchByIfsc(ifscCode: string): Observable<any> {
        const fdmApiBaseUrl = environment.preprod
            ? 'https://api.lokos.in/'
            : environment.fdmApiBaseUrl;
        const masterUrl = `${fdmApiBaseUrl}${SERVICE_URL.MASTER_CONFIG}`;
        const url = `${masterUrl}branch/getAllBankBranchByIFSCCode/${ifscCode}`;
        return this.httpService.getRecords(url);
    }

    // Approve Profile and Reject Profile Process
    approveProfile(data: any): Observable<any> {
        const url = `${this.baseUrl}shg/${data.shg_uuid}/approval`;
        return this.http.post(url, data, { observe: 'body' });
    }

    // Get Image Path
    getImagePath(data: any): Observable<any> {
        const url = `${this.baseUrl}shg/${data.imageName}`;
        return this.http.post(url, data, { observe: 'body' });
    }
    // Get Image Full URL
    getImageFullUrl(data: any): Observable<any> {
        return this.http
            .get(data, {
                observe: 'response',
                responseType: 'blob',
            })
            .pipe(
                map((response: any) => {
                    // Validate response status
                    if (response.status !== 200) {
                        throw new Error(
                            `Invalid response status: ${response.status}`
                        );
                    }
                    return response.body;
                }),
                catchError((error: any) => {
                    console.error('Error fetching full URL image:', error);
                    const errorMessage = error.status
                        ? `HTTP ${error.status}: ${error.statusText || 'Failed to load image'}`
                        : error.message || 'Failed to load image';
                    return throwError(() => new Error(errorMessage));
                })
            );
    }

    // Get SHG Address Details
    GetShgAddressDetails(shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/address`;
        return this.httpService.getRecords(url);
    }

    // Get shg designation
    GetShgDesignationList(shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/designation`;
        return this.httpService.getRecords(url);
    }

    // Get shg other detail
    GetShgOtherDetails(shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/tags`;
        return this.httpService.getRecords(url);
    }

    // Get Member Basic Details
    GetMemberBasicDetails(shgId: any, memberId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/basic`;
        return this.httpService.getRecords(url);
    }

    // Get Member Bank Details
    GetMemberBankDetails(shgId: any, memberId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/bank`;
        return this.httpService.getRecords(url);
    }

    // Get Member KYC Details
    GetMemberKycDetails(memberId: any, shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/kyc`;
        return this.httpService.getRecords(url);
    }

    // Get Member Address Details
    GetMemberAddressDetails(memberId: any, shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/address`;
        return this.httpService.getRecords(url);
    }

    // Get Member Insurance Details
    GetMemberInsuranceDetails(memberId: any, shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/insurance`;
        return this.httpService.getRecords(url);
    }
    // Get Member Mobile Details
    GetMemberMobileDetails(memberId: any, shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/mobile`;
        return this.httpService.getRecords(url);
    }

    // Get Member Cadre Details
    GetMemberCadreDetails(memberId: any, shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/cadre`;
        return this.httpService.getRecords(url);
    }

    // Get Member Other Details (Tags)
    GetMemberOtherDetails(memberId: any, shgId: any): Observable<any> {
        const url = `${this.baseUrl}shg/${shgId}/member/${memberId}/tags`;
        return this.httpService.getRecords(url);
    }

    // Member Approve Profile and Reject Profile Process
    memberApproveProfile(data: any): Observable<any> {
        const url = `${this.baseUrl}shg/${data.shg_uuid}/member/${data.member_uuid}/approval`;
        return this.http.post(url, data, { observe: 'body' });
    }

    getMeetingData(shgId: any, meetingNumber: any): Observable<any> {
        const inputData = { shgId: shgId, meetingNo: meetingNumber };

        const url = `${this.baseTransUrl}meeting/MeetingsBySHG`;
        return this.httpService.getRecords(url, inputData);
    }

    putsearchMemberAngular<T = any>(data: any): Observable<T> {
        const url = `${this.baseUrl}member`;
        return this.http.put<T>(url, data, { observe: 'body' });
    }

    // Search SHG
    putsearchShgAngular<T = any>(params: any): Observable<T> {
        const url = `${this.baseUrl}shg`;
        return this.http.get<T>(url, { params });
    }

    getShgGroupCode(cboUuid: string): Observable<any> {
        const url = `${this.baseUrl}shg/${cboUuid}/cbo`;
        return this.httpService.getRecords(url);
    }
}
